import { useState } from 'react';
import { Text, Group, CopyButton, Button, Divider, Alert, Badge, Table, ActionIcon } from '@mantine/core';
import { IconCheck, IconCopy, IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import SettingsApi from '../../api/SettingsApi';
import { showWpSyncWarning } from '../GeneralComponent/GeneralComponent';

interface DNSInstructionsProps {
    configData: any;
    websiteId: string;
    onVerificationSuccess: () => void;
    onConfigChange?: () => void;
    onDisconnect?: () => void;
    disconnecting?: boolean;
    isAlreadyActive?: boolean;
}

export function DNSInstructions({ configData, websiteId, onVerificationSuccess, onConfigChange, onDisconnect, disconnecting, isAlreadyActive }: DNSInstructionsProps) {
    const [verifying, setVerifying] = useState(false);

    const handleVerify = () => {
        setVerifying(true);
        SettingsApi.verifycustomhostname({ website_id: websiteId })
            .then(res => res.json())
            .then(data => {
                setVerifying(false);
                if (data.status === 1) {
                    notifications.show({
                        title: 'Success!',
                        message: data.message,
                        color: 'green',
                        icon: <IconCircleCheck />
                    });
                    showWpSyncWarning(data, websiteId);
                } else {
                    notifications.show({
                        title: 'Verification In Progress',
                        message: data.message,
                        color: 'blue'
                    });
                }
                onVerificationSuccess();
                if (onConfigChange) onConfigChange();
            })
            .catch(() => {
                setVerifying(false);
                notifications.show({
                    title: 'Error',
                    message: 'Network error occurred',
                    color: 'red'
                });
            });
    };

    const overallStatus = configData.custom_hostname_status;
    const isActive = overallStatus === 'active';
    const sslStatus = configData.ssl_status || 'pending';
    const showAsActive = isActive || isAlreadyActive;

    const CopyBtn = ({ value }: { value: string }) => (
        <CopyButton value={value || ''} timeout={2000}>
            {({ copied, copy }) => (
                <ActionIcon
                    color={copied ? 'teal' : 'blue'}
                    variant="light"
                    onClick={(e) => { e.stopPropagation(); copy(); }}
                    size="sm"
                >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                </ActionIcon>
            )}
        </CopyButton>
    );

    return (
        <>
            <Divider my="lg" />

            <Group spacing="xs" mb="md">
                <Text size="md" fw={600}>DNS Configuration</Text>
                {showAsActive ? (
                    <Badge color="green" variant="filled">Active</Badge>
                ) : (
                    <Badge color="orange" variant="filled">Pending</Badge>
                )}
            </Group>

            {isAlreadyActive && !isActive ? (
                <Alert icon={<IconCircleCheck size={16} />} color="green" mb="md">
                    <Text size="sm" fw={600} mb={4}>Domain Already Active</Text>
                    <Text size="sm">
                        This domain (<b>{configData?.custom_hostname}</b>) is already registered and active in Cloudflare.
                        Your custom tracking domain is working — no DNS changes are needed.
                    </Text>
                </Alert>
            ) : !showAsActive ? (
                <Alert icon={<IconAlertCircle size={16} />} color="blue" mb="md">
                    Add the following DNS records to your domain registrar to activate your custom tracking domain.
                </Alert>
            ) : (
                <Alert icon={<IconCircleCheck size={16} />} color="green" mb="sm">
                    Your custom tracking domain is now active. Advanced tracking is enabled and working.
                </Alert>
            )}
            {!isAlreadyActive && (
                <Alert icon={<IconAlertCircle size={16} />} color="yellow" mb="md">
                    <Text size="sm" fw={600} mb={4}>Important: Cloudflare Users</Text>
                    <Text size="sm" mb={6}>
                        If you use Cloudflare for <b>{configData?.custom_hostname}</b>, make sure to disable the proxy (orange cloud) on your CNAME record:
                    </Text>
                    <Text size="sm" component="div" style={{ lineHeight: 1.8, paddingLeft: 8 }}>
                        1. Go to your <b>Cloudflare Dashboard</b><br />
                        2. Navigate to <b>DNS → Records</b><br />
                        3. Click <b>Add Record</b> and select type <b>CNAME</b><br />
                        4. Enter your hostname and target as instructed below<br />
                        5. <b>Before saving</b>, set <b>Proxy Status</b> to <b>DNS Only</b> (grey cloud 🌥️)<br />
                        6. Click <b>Save</b>
                    </Text>
                    <Text size="sm" mt={6} c="dimmed">
                        ⚠️ Do not enable the orange cloud (Proxied) — it will cause caching conflicts and cache purging will not work correctly.
                    </Text>
                </Alert>
            )}

            {/* DNS Records Table */}
            {!isAlreadyActive && (
                <div style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginBottom: '16px'
                }}>
                    <Table horizontalSpacing="md" verticalSpacing="sm" striped highlightOnHover>
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                                <th style={{ width: '80px' }}>Type</th>
                                <th>Name</th>
                                <th>Value</th>
                                <th style={{ width: '80px', textAlign: 'center' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><Text size="sm" ff="monospace">TXT</Text></td>
                                <td>
                                    <Group spacing={4} style={{ flexWrap: 'nowrap' }}>
                                        <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>{configData.txt_record_name}</Text>
                                        <CopyBtn value={configData.txt_record_name} />
                                    </Group>
                                </td>
                                <td>
                                    <Group spacing={4} style={{ flexWrap: 'nowrap' }}>
                                        <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>{configData.txt_record_value}</Text>
                                        <CopyBtn value={configData.txt_record_value} />
                                    </Group>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    {sslStatus === 'active' ? (
                                        <Badge color="green" variant="filled" size="sm">Active</Badge>
                                    ) : (
                                        <Badge color="orange" variant="filled" size="sm">Pending</Badge>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td><Text size="sm" ff="monospace">CNAME</Text></td>
                                <td>
                                    <Group spacing={4} style={{ flexWrap: 'nowrap' }}>
                                        <Text size="sm" ff="monospace">{configData.custom_subdomain || configData.subdomain}</Text>
                                        <CopyBtn value={configData.custom_subdomain || configData.subdomain} />
                                    </Group>
                                </td>
                                <td>
                                    <Group spacing={4} style={{ flexWrap: 'nowrap' }}>
                                        <Text size="sm" ff="monospace">{configData.cname_target}</Text>
                                        <CopyBtn value={configData.cname_target} />
                                    </Group>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    {isActive ? (
                                        <Badge color="green" variant="filled" size="sm">Active</Badge>
                                    ) : (
                                        <Badge color="orange" variant="filled" size="sm">Pending</Badge>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            )}

            <Group position="apart">
                {!showAsActive && (
                    <Button
                        onClick={(e) => { e.stopPropagation(); handleVerify(); }}
                        loading={verifying}
                        color="blue"
                    >
                        Check Connection
                    </Button>
                )}
                {onDisconnect && (
                    <Button
                        onClick={(e) => { e.stopPropagation(); onDisconnect(); }}
                        loading={disconnecting}
                        color="red"
                        variant="outline"
                        ml={isActive ? 'auto' : undefined}
                    >
                        Disconnect Advanced Tracking
                    </Button>
                )}
            </Group>

            {!showAsActive && (
                <Text size="xs" color="dimmed" mt="sm" style={{ lineHeight: 1.5 }}>
                   <b>Note:</b> If you have added the TXT record and CNAME correctly but the status still shows Pending, please wait anywhere from 1 minute to as long as several hours. Sometimes the system needs a little time to propagate DNS changes.
                </Text>
            )}
            {/*
              Shopify-only follow-up note. Rendered when the parent website is a
              Shopify site (front_end_framework === 2). The Check Connection
              backend re-injects the universal smart code into the merchant's
              Shopify metafields on success, so this hint tells the user how to
              force that refresh if their store hasn't picked it up yet.
            */}
            {Number(configData?.front_end_framework) === 2 && (
                <Text size="xs" color="dimmed" mt="xs" style={{ lineHeight: 1.5 }}>
                   <b>Note (Shopify):</b> If Advance S2S is properly connected but the tracking domain has not been updated on your page, then click the &quot;Check Connection&quot; button.
                </Text>
            )}
        </>
    );
}

export default DNSInstructions;
